import { ProtectedRoute } from "@components/etc/Restricted";
import { lazy, Suspense, type ReactNode } from "react";
import { Navigate, Route, Routes, useParams } from "react-router";
import AppShell from "@shell/AppShell";

const Home = lazy(() => import("@screens/home/HomePage"));
const EmulatorsPage = lazy(() => import("@screens/emulators/EmulatorsPage"));
const EmulatorView = lazy(() => import("@screens/emulator-view/EmulatorView"));
const EmulatorPopout = lazy(() => import("@screens/emulator-view/EmulatorPopout"));
const WorkspacesPage = lazy(() => import("@screens/workspaces/WorkspacesPage"));
const BuildsPage = lazy(() => import("@screens/builds/BuildsPage"));
const OrgPage = lazy(() => import("@screens/org/OrgPage"));
const ToolPage = lazy(() => import("@screens/org/ToolPage"));
const AccountPage = lazy(() => import("@screens/account/AccountPage"));
const PlatformPage = lazy(() => import("@screens/platform/PlatformPage"));
const TenantsPage = lazy(() => import("@screens/platform/TenantsPage"));
const TenantDetail = lazy(() => import("@screens/platform/TenantDetail"));
const OnboardTenant = lazy(() => import("@screens/platform/OnboardTenant"));
const LoginPage = lazy(() => import("@screens/auth/LoginPage"));
const LinkGitLab = lazy(() => import("@screens/auth/LinkGitLab"));
const VcsLinkRedirect = lazy(() => import("@screens/account/VcsLinkRedirect"));
const CliPage = lazy(() => import("@screens/cli/CliPage"));
const OnboardingInit = lazy(() => import("@screens/onboarding/OnboardingInit"));

const page = (el: ReactNode) => <Suspense fallback={null}>{el}</Suspense>;

/** `/account/:tenantId/:section` → `/org/:section` (tenant switched on arrival). */
function LegacyTenantAdmin() {
  const { tenantId, section } = useParams();
  const map: Record<string, string> = { overview: "members", members: "members", groups: "groups", resources: "access", permissions: "access" };
  return <Navigate to={`/org/${map[section ?? ""] ?? "members"}?tenant=${tenantId}`} replace />;
}

function LegacyEmulator() {
  const { id } = useParams();
  return <Navigate to={id ? `/emulators/${id}` : "/emulators"} replace />;
}

export default function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={page(<LoginPage />)} />
      <Route path="/onboarding/init" element={page(<OnboardingInit />)} />

      <Route element={<ProtectedRoute />}>
        <Route path="/emulator-popout" element={page(<EmulatorPopout />)} />
        <Route path="/link/gitlab" element={page(<LinkGitLab />)} />
        <Route path="/account/vcs-link-redirect" element={page(<VcsLinkRedirect />)} />

        <Route element={<AppShell />}>
          <Route index element={page(<Home />)} />
          <Route path="/emulators" element={page(<EmulatorsPage />)} />
          <Route path="/emulators/:id" element={page(<EmulatorView />)} />
          <Route path="/workspaces" element={page(<WorkspacesPage />)} />
          <Route path="/builds" element={page(<BuildsPage />)} />
          <Route path="/org" element={<Navigate to="/org/members" replace />} />
          <Route path="/org/tools/:tool" element={page(<ToolPage />)} />
          <Route path="/org/:tab" element={page(<OrgPage />)} />
          <Route path="/account" element={<Navigate to="/account/profile" replace />} />
          <Route path="/account/:tab" element={page(<AccountPage />)} />
          <Route path="/cli" element={page(<CliPage />)} />
          <Route path="/platform" element={<Navigate to="/platform/overview" replace />} />
          <Route path="/platform/tenants" element={page(<TenantsPage />)} />
          <Route path="/platform/tenants/new" element={page(<OnboardTenant />)} />
          <Route path="/platform/tenants/:id" element={page(<TenantDetail />)} />
          <Route path="/platform/:tab" element={page(<PlatformPage />)} />

          {/* legacy paths */}
          <Route path="/install" element={<Navigate to="/cli" replace />} />
          <Route path="/account/cli" element={<Navigate to="/cli" replace />} />
          <Route path="/dev/emulators/:id?" element={<LegacyEmulator />} />
          <Route path="/dashboards/snapshots" element={<Navigate to="/builds" replace />} />
          <Route path="/account/:tenantId/:section" element={<LegacyTenantAdmin />} />
          <Route path="/admin" element={<Navigate to="/platform/overview" replace />} />
          <Route path="/admin/onboard" element={<Navigate to="/platform/tenants/new" replace />} />
          <Route path="/admin/tenants/:id?" element={<Navigate to="/platform/tenants" replace />} />
          <Route path="/admin/*" element={<Navigate to="/platform/overview" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Route>
    </Routes>
  );
}
